import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core'
import {
  DEFAULT_DISTANCE,
  DEFAULT_ORDER,
  FilterCriteria,
  ProDirectory,
  proDirectoryGqlToProDirectory,
} from './pro.directory.types'
import {
  PostalCodeFieldsFragment,
  ProDirectory_AutocompleteGeoSearchGql,
  ProDirectory_SearchGql,
  RegionFieldsFragment,
  SearchProDirectoryCriteria,
  SearchProDirectoryOrder,
  SearchProDirectorySorting,
} from '../../../../__generated__/types'
import {faCar} from '@fortawesome/pro-solid-svg-icons/faCar'
import {faInfoCircle} from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import {faMapMarkerAlt} from '@fortawesome/pro-solid-svg-icons/faMapMarkerAlt'
import {SortingOption, SortingOptions} from '../../../craft/sorting/sorting.types'
import {BehaviorSubject, Observable, of, Subject} from 'rxjs'
import {ApplicationRoute} from '../../../craft/utils/application.route'
import {PUBLIC_ROUTES} from '../../routing/public.routes'
import {LinkService, OTHER_OFFERING_ID} from '../../../core/i18n/link.service'
import {combineLatestWith, distinctUntilChanged, filter, map, switchMap, takeUntil, tap} from 'rxjs/operators'
import {ContactForm} from '../../../core/content/types/contact-form.types'
import {HashMap, TranslateParams, TranslocoService} from '@ngneat/transloco'
import {FilterOption, FilterOptions} from '../../../craft/filter/filter.types'
import {pickProperty} from '../../../core/graphql/graphql-utils'
import {FilterComponent} from '../../../craft/filter/filter.component'
import {Region} from '../../../core/content/types/craftsmen-region-types'
import {Logger} from '../../../core/logging/logger.types'

@Component({
  selector: 'bgo-pro-directory',
  templateUrl: './pro-directory.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }
  `,
})
export class ProDirectoryComponent implements OnInit, OnDestroy {
  private _originalFilterCriteria!: FilterCriteria

  @Input() set filter(f: FilterCriteria) {
    this._originalFilterCriteria = f
    if (f.location) {
      this.locationDefaultValue.set(this.toLocationFilterOption(f.location))
      if (f.location.distance) {
        const distance = f.location.distance
        const existingOption = this.distanceOptions.find(o => o.value === distance)
        if (existingOption) {
          this.distanceDefaultOption = existingOption
        } else {
          this.logger.warn(
            `${distance} is not a valid distance implemented by pro-directory.component, default is applied`,
          )
          f.location.distance = DEFAULT_DISTANCE
        }
      }
    }
    if (f.region) {
      this.regionDefaultValue.set(this.toRegionFilterOption(f.region))
    }
    this.filter$.next({
      ...this.filter$.value,
      ...f,
      location: f.location
        ? {
            ...f.location,
            // in the API, distance is a required parameter - use default if we don't have any provided in input
            distance: f.location.distance ? f.location.distance : DEFAULT_DISTANCE,
          }
        : undefined,
      sortBy: f.geoLocation ? SearchProDirectorySorting.DistanceAsc : DEFAULT_ORDER,
      // if the region is set, the location is ignored
      region: f.region?.postalCodes
        ? {
            postalCodes: f.region?.postalCodes,
          }
        : undefined,
    })
  }

  @Input() title!: string
  @Output() navigated = new EventEmitter()
  @Output() searchEnd = new EventEmitter<{total: number}>()

  protected proDirectory?: ProDirectory
  protected readonly infoIcon = faInfoCircle
  protected readonly locationIcon = faMapMarkerAlt
  protected readonly distanceIcon = faCar
  protected readonly isLoading$ = new BehaviorSubject(false)

  protected distanceDefaultOption = {
    name: this.t('proDirectory.distanceFilter.defaultLabel'),
    value: DEFAULT_DISTANCE,
  }
  protected readonly distanceOptions = [
    // backend expects distance in meters, but for UX, we show minutes
    // we consider 1km = 1min
    {name: this.t('proDirectory.distanceFilter.15KmLabel'), value: 15000},
    {name: this.t('proDirectory.distanceFilter.30KmLabel'), value: 30000},
    {name: this.t('proDirectory.distanceFilter.60KmLabel'), value: 60000},
    {name: this.t('proDirectory.distanceFilter.90KmLabel'), value: 90000},
  ]
  protected locationDefaultValue = signal<FilterOption<PostalCodeFieldsFragment> | undefined>(undefined)
  protected regionDefaultValue = signal<FilterOption<Omit<RegionFieldsFragment, 'regionName'>> | undefined>(undefined)

  protected defaultGeoValue = computed<FilterOption<unknown> | undefined>(() => {
    return this.regionDefaultValue() ? this.regionDefaultValue() : this.locationDefaultValue()
  })

  protected readonly sortingOptions: SortingOptions = [
    {
      name: this.t('proDirectory.sortBy.relevance'),
      value: SearchProDirectorySorting.RelevanceDesc,
      default: true,
    },
    {
      name: this.t('proDirectory.sortBy.distance'),
      value: SearchProDirectorySorting.DistanceAsc,
    },
  ]

  protected readonly filter$ = new BehaviorSubject<SearchProDirectoryCriteria & SearchProDirectoryOrder>({
    sortBy: DEFAULT_ORDER,
  })
  protected otherJobRequestRoute = new ApplicationRoute(
    PUBLIC_ROUTES.JOB_REQUEST.path.replace(':offeringId', OTHER_OFFERING_ID),
  )
  protected readonly proDirectoryIsLoading$ = new BehaviorSubject<boolean>(false)

  protected readonly filterHasChanged$: Observable<ProDirectory> = this.filter$.pipe(
    distinctUntilChanged((p, c) => {
      if (c.industryIds && !p.industryIds?.every(i => c.industryIds?.includes(i))) {
        return false
      }

      if (c.offeringIds && !p.offeringIds?.every(o => c.offeringIds?.includes(o))) {
        return false
      }

      if ((p.searchText || c.searchText) && p.searchText !== c.searchText) {
        return false
      }

      if ((p.location || c.location) && p.location?.zip !== c.location?.zip) {
        return false
      }

      if ((p.location || c.location) && p.location?.distance !== c.location?.distance) {
        return false
      }

      if ((p.region || c.region) && !regionsAreEqual(p.region, c.region)) {
        return false
      }

      if (
        ((c.geoLocation?.lng && c.geoLocation.lat) || c.location?.zip || c.region?.postalCodes.length) &&
        p.sortBy !== c.sortBy
      ) {
        return false
      }
      // no change found, won't emit
      return true
    }),
    switchMap(({sortBy, geoLocation, ...filters}) => {
      return this.proDirectoryGql
        .fetch(
          {
            criteria: {
              industryIds: filters.industryIds,
              offeringIds: filters.offeringIds,
              searchText: filters.searchText,
              region: filters.region,
              location: filters.location,
            },
            page: {
              offset: 0,
              limit: 5,
            },
            order: {
              geoLocation,
              sortBy,
            },
          },
          {fetchPolicy: 'cache-first'},
        )
        .pipe(combineLatestWith(of({sortBy, geoLocation, ...filters})))
    }),
    // directory can be sorted when a location filter is set
    tap(([_, filter]) => (this.canSort = !!filter.location || !!filter.region)),
    map(([res]) => {
      return {
        pros: res.data.searchProDirectory.items.map(proDirectoryGqlToProDirectory),
        total: res.data.searchProDirectory.total,
      }
    }),
    tap(proDirectory => this.searchEnd.emit({total: proDirectory.total})),
  )

  protected canSort = false
  protected readonly dataPrivacyLink = this.linkService.localizedPathTo(PUBLIC_ROUTES.DATA_PRIVACY)

  private readonly phoneNumber = this.transloco.translate('jobRequestBottomNavigation.phoneNumber')
  private readonly unsubscribe$ = new Subject<void>()

  @ViewChild('locationFilter') private readonly locationFilter!: FilterComponent
  @ViewChild('distanceFilter') private readonly distanceFilter!: FilterComponent

  protected readonly contactForm: ContactForm = {
    callToAction2: {label: this.phoneNumber, type: 'Phone', value: this.phoneNumber},
    callToAction1: {
      label: this.transloco.translate('jobRequestBottomNavigation.jobRequestCta'),
      type: 'Path',
      value: this.otherJobRequestRoute,
    },
    alternativeCallToActionTitle: this.transloco.translate('jobRequestBottomNavigation.contactDialog.alternativeLabel'),
  }

  constructor(
    private readonly transloco: TranslocoService,
    private readonly proDirectoryGql: ProDirectory_SearchGql,
    private readonly searchPostCodeGql: ProDirectory_AutocompleteGeoSearchGql,
    protected readonly linkService: LinkService,
    private readonly cdr: ChangeDetectorRef,
    private readonly logger: Logger,
  ) {}

  ngOnInit(): void {
    this.filterHasChanged$.pipe(takeUntil(this.unsubscribe$)).subscribe(proDirectory => {
      this.proDirectory = proDirectory
      this.cdr.detectChanges()
    })
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  searchPostCode(searchText: string): Observable<FilterOptions<unknown>> {
    return this.searchPostCodeGql.fetch({searchText, limit: 5}).pipe(
      takeUntil(this.unsubscribe$),
      filter(res => !!res.data),
      map(res => pickProperty(res.data, 'searchGeography')),
      map(results => {
        return {
          isLoading: false,
          options: results?.map(res => this.geoResultOptions(res)) || [],
        }
      }),
    )
  }

  locationFilterChanged(filterOption?: FilterOption<unknown>) {
    const filterValue = filterOption?.value as LocationFilterOptionValue | undefined
    switch (filterValue?.__typename) {
      case 'PostalCode': {
        const distance = this.distanceFilter?.value() || this.distanceDefaultOption
        this.filter$.next({
          ...this.filter$.value,
          location: {
            postalDistrict: filterValue.postalDistrict,
            zip: filterValue.zip,
            distance: distance.value as number,
          },
          // filtering by location, and not region
          region: undefined,
        })
        break
      }
      case 'Region': {
        const postalCodes = filterValue.postalCodes.filter((p): p is number => p !== undefined)
        this.filter$.next({
          ...this.filter$.value,
          region: {
            postalCodes,
          },
          // filtering by region, and not location
          location: undefined,
        })
        break
      }
      default: {
        this.filter$.next({
          ...this.filter$.value,
          // cleared filters
          location: undefined,
          region: undefined,
        })
      }
    }
  }

  distanceFilterChanged(filterOption?: FilterOption<unknown>) {
    const currentFilter = this.filter$.value

    // distance only makes sense with a location
    if (typeof filterOption?.value === 'number' && currentFilter.location) {
      this.filter$.next({
        ...currentFilter,
        location: {...currentFilter.location, distance: filterOption.value},
      })
    }
  }

  sortBy(sortingOption: SortingOption): void {
    this.filter$.next({...this.filter$.value, sortBy: sortingOption.value as SearchProDirectorySorting})
  }

  loadMoreDirectoryPros() {
    this.proDirectoryIsLoading$.next(true)

    const {sortBy, geoLocation, ...criteria} = this.filter$.value

    this.proDirectoryGql
      .fetch(
        {
          criteria,
          page: {
            limit: 3,
            offset: this.proDirectory?.pros.length,
          },
          order: {
            geoLocation,
            sortBy: sortBy ?? DEFAULT_ORDER,
          },
        },
        {fetchPolicy: 'cache-first'},
      )
      .pipe(
        map(res => {
          return {
            pros: res.data.searchProDirectory.items.map(proDirectoryGqlToProDirectory),
            total: res.data.searchProDirectory.total,
          }
        }),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(proRepository => {
        this.proDirectoryIsLoading$.next(false)
        this.proDirectory = {
          pros: [...(this.proDirectory?.pros ?? []), ...proRepository.pros],
          total: proRepository.total,
        }
      })
  }

  navigate() {
    this.navigated.emit()
  }

  t(key: TranslateParams, params?: HashMap<unknown>, lang?: string) {
    return this.transloco.translate(key, params, lang)
  }

  clearLocationFilters() {
    this.filter = {...this._originalFilterCriteria, location: undefined}
    this.locationFilter.clear()
    this.distanceFilter.selected(this.distanceDefaultOption)
  }

  private toLocationFilterOption(location: {
    zip: number
    postalDistrict: string
  }): FilterOption<PostalCodeFieldsFragment> {
    return {
      name: `${location.zip} ${location.postalDistrict}`,
      value: {
        __typename: 'PostalCode',
        ...location,
      },
    }
  }

  private toRegionFilterOption(region: Region): FilterOption<Omit<RegionFieldsFragment, 'regionName'>> {
    return {
      name: this.t('proDirectory.locationFilter.regionSuggestion', {regionName: region.regionName}),
      value: {
        __typename: 'Region',
        postalCodes: region.postalCodes,
      },
    }
  }

  private geoResultOptions(
    item?: PostalCodeFieldsFragment | RegionFieldsFragment,
  ): FilterOption<LocationFilterOptionValue> {
    switch (item?.__typename) {
      case 'Region':
        return {
          value: item,
          name: this.t('proDirectory.locationFilter.regionSuggestion', {regionName: item.regionName}),
        }
      case 'PostalCode':
        return this.toLocationFilterOption(item)
    }
    throw Error('unsupported geo option')
  }
}

/**
 * Location filter can either be a postal code or a region, based on user choice
 * - Region will filter based on a list of postal codes
 * - Postal code will filter around a single postal code and ask the user for a radius distance
 */
type LocationFilterOptionValue = PostalCodeFieldsFragment | RegionFieldsFragment

function regionsAreEqual(r1?: {postalCodes: number[]}, r2?: {postalCodes: number[]}) {
  if (r1?.postalCodes.length !== r2?.postalCodes.length) {
    return false
  }

  // compare postal code arrays
  const array1 = r1?.postalCodes ?? []
  const array2 = r2?.postalCodes ?? []

  // sort both arrays
  const sortFn = (a: number, b: number) => a - b
  const sortedArray1 = [...array1].sort(sortFn)
  const sortedArray2 = [...array2].sort(sortFn)

  // compare sorted arrays
  return sortedArray1.every((value, index) => value === sortedArray2[index])
}
